import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    createTheme,
    FormControlLabel,
    FormLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material';
import { gotoNextMenu, gotoPreviousMenu } from '../../../store/assessment.slice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { CulturalConsiderationsModel } from '../../../Models/CulturalConsiderations/CulturalConsiderationsModel.model';
import { Formik, Form, FormikProps } from 'formik';
import {
    fetchCulturalConsiderationsInfo,
    fetchCulturalConsiderationsInfoByMemberId,
    saveCulturalConsiderationsInfo,
    setCulturalConsiderationsInfo,
} from '../../../store/culturalConsiderationsSlice.slice';
import { copyObject, highlightStyle } from '../../../utils/common';
import { StatusEnum } from '../../../Enum/StatusEnum';
import BHDiagnosticConfirmationBody from '../../Shared/BHDiagnosticConfirmationBody';
import { NO, YES } from '../../../utils/constants';
import { DiagnosticType } from '../../../Enum/DiagnosticType';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { isAIIcon } from '../Assessment';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Cultural_Considerations_Eating_Disorder_GAF_Scale.pdf';

const theme = createTheme();
const classes = {
    form: {
        '& .MuiCheckbox-root': {
            marginLeft: '-10px',
        },
    },
    root: {
        '& .MuiGrid-root': {
            marginBottom: theme.spacing(2),
        },
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '90%',
        },
    },
};

const yesNoList = [{ Answer: YES }, { Answer: NO }];

const CulturalConsiderations: React.FC<{ memberId: string }> = ({ memberId }) => {
    const params = useParams();
    memberId = params.memberId;
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { engagementDraft, memberAssessmentHistory, isSaving: isSavingEngagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const isBHDiagnostic = engagementDraft.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;
    const { culturalConsiderationsInfo } = useSelector((state: RootState) => state.culturalConsiderationsSlice);
    const isSaving = isSavingEngagementDraft;
    const [isChanged, setIsChanged] = useState<boolean>(false);

    const culturalConsiderationsIsComplete = (values: CulturalConsiderationsModel) => {
        const isComplete = isBHDiagnostic
            ? values.CulturalEthnicInfoConcerns?.trim()?.length > 0 && (values.CulturalBarriers?.Name?.trim()?.length > 0 || values.NoBarriers)
            : true;
        return isComplete;
    };

    const handleSubmit = () => {
        setShowSaveAlert(true);
    };

    const handleOnConfirmToNext = (culturalConsiderationsInfo: CulturalConsiderationsModel, isComplete = false) => {
        const copy = copyObject(culturalConsiderationsInfo);
        copy.MemberId = memberId;
        copy.IsAssessmentCompleted = isComplete;
        if (Boolean(isComplete)) {
            copy.AssessmentStatus = StatusEnum.Completed;
            dispatch(saveCulturalConsiderationsInfo(copy, false, true));
        } else {
            copy.AssessmentStatus = StatusEnum.InProgress;
            dispatch(saveCulturalConsiderationsInfo(copy, true, true));
        }
    };

    const handleOnConfirmSkip = () => {
        dispatch(setCulturalConsiderationsInfo(new CulturalConsiderationsModel()));
        dispatch(gotoNextMenu());
    };

    useEffect(() => {
        if (engagementDraft?.CulturalConsiderationsId) {
            dispatch(fetchCulturalConsiderationsInfo(engagementDraft.CulturalConsiderationsId));
        } else {
            dispatch(fetchCulturalConsiderationsInfoByMemberId(memberId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Formik initialValues={culturalConsiderationsInfo} onSubmit={handleSubmit} enableReinitialize={true}>
            {({ values, setFieldValue, errors, handleChange }: FormikProps<CulturalConsiderationsModel>) => {
                const handleFieldChange = (e: React.ChangeEvent<any>) => {
                    const aiFlagFieldByName: Record<string, keyof CulturalConsiderationsModel> = {
                        MeaningfulActivities: 'IsAIMeaningfulActivities',
                        CommunitySupportSelfHelp: 'IsAICommunitySupportSelfHelp',
                        SpiritualOrReligiousAffiliation: 'IsAISpiritualOrReligiousAffiliation',
                        PreferredReligiousOrBeliefSystem: 'IsAIPreferredReligiousOrBeliefSystem',
                        PreferredReligiousOrBeliefSystemAnswer: 'IsAIPreferredReligiousOrBeliefSystemAnswer',
                        CulturalEthnicInfoConcerns: 'IsAICulturalEthnicInfoConcerns',
                        'CulturalBarriers.Name': 'IsAICulturalBarriers',
                    };

                    const aiFlagField = aiFlagFieldByName[e.target.name];
                    if (aiFlagField) {
                        setFieldValue(aiFlagField, false);
                    }
                    setIsChanged(true);
                    handleChange(e);
                };
                return (
                    <Form>
                        <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
                            <Grid container sx={classes.form} direction="column" spacing={2} className="content-panel">
                                <Grid>
                                    {isSaving ? (
                                        <Box display="flex" justifyContent="center" className="loader-container">
                                            <CircularProgress />
                                        </Box>
                                    ) : (
                                        <>
                                            <Grid container spacing={2} sx={classes.root}>
                                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                                        <Typography variant="h6">Cultural Considerations</Typography>
                                                        <Tooltip title="Cultural Considerations Help Document">
                                                            <InfoIcon
                                                                color="primary"
                                                                fontSize="small"
                                                                cursor="pointer"
                                                                onClick={() => window.open(`${HelpPage}`, '_blank')}
                                                            />
                                                        </Tooltip>
                                                    </Stack>
                                                    {isBHDiagnostic && (
                                                        <Typography variant="body2" component="label" ml={5} sx={highlightStyle}>
                                                            * Required for BH Diagnostic
                                                        </Typography>
                                                    )}
                                                    <hr />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel>{AssessmentQuestions.CulturalConsiderations_MeaningfulActivities}</FormLabel>
                                                    {culturalConsiderationsInfo.IsAIMeaningfulActivities &&
                                                        isAIIcon(culturalConsiderationsInfo.MeaningfulActivities)}
                                                </Grid>
                                                <Grid size={6}>
                                                    <TextField
                                                        label=""
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        multiline
                                                        rows={4}
                                                        placeholder="(community involvement, volunteer activities, leisure/recreation, other interests)"
                                                        margin="normal"
                                                        name="MeaningfulActivities"
                                                        value={values.MeaningfulActivities || ''}
                                                        onChange={handleFieldChange}
                                                    />
                                                </Grid>
                                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                                    <hr className="hr-lite" />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel>{AssessmentQuestions.CulturalConsiderations_CommunitySelfHelp}</FormLabel>
                                                    {culturalConsiderationsInfo.IsAICommunitySupportSelfHelp && isAIIcon(culturalConsiderationsInfo.CommunitySupportSelfHelp)}
                                                </Grid>
                                                <Grid size={6}>
                                                    <TextField
                                                        label=""
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        multiline
                                                        rows={4}
                                                        placeholder="(AA, NA, etc.)"
                                                        margin="normal"
                                                        name="CommunitySupportSelfHelp"
                                                        value={values.CommunitySupportSelfHelp || ''}
                                                        onChange={handleFieldChange}
                                                    />
                                                </Grid>
                                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                                    <Typography variant="h6" component="label">
                                                        Religion/Spiritual/Belief Systems
                                                    </Typography>
                                                    <hr />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel>{AssessmentQuestions.CulturalConsiderations_SpiritualReligious}</FormLabel>
                                                    {culturalConsiderationsInfo.IsAISpiritualOrReligiousAffiliation && isAIIcon(culturalConsiderationsInfo.SpiritualOrReligiousAffiliation)}
                                                </Grid>
                                                <Grid size={6}>
                                                    <RadioGroup
                                                        row
                                                        name="SpiritualOrReligiousAffiliation"
                                                        value={values.SpiritualOrReligiousAffiliation || ''}
                                                        onChange={handleFieldChange}
                                                    >
                                                        {yesNoList.map((type, index) => {
                                                            return (
                                                                <FormControlLabel
                                                                    key={`answer-label-${index}`}
                                                                    value={type.Answer}
                                                                    control={<Radio />}
                                                                    label={type.Answer}
                                                                />
                                                            );
                                                        })}
                                                    </RadioGroup>
                                                </Grid>
                                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                                    <hr className="hr-lite" />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel>{AssessmentQuestions.CulturalConsiderations_PreferredBelief}</FormLabel>
                                                    {culturalConsiderationsInfo.IsAIPreferredReligiousOrBeliefSystem && isAIIcon(culturalConsiderationsInfo.PreferredReligiousOrBeliefSystem)}
                                                </Grid>
                                                <Grid size={6}>
                                                    <RadioGroup
                                                        row
                                                        name="PreferredReligiousOrBeliefSystem"
                                                        value={values.PreferredReligiousOrBeliefSystem || ''}
                                                        onChange={handleFieldChange}
                                                    >
                                                        {yesNoList.map((type, index) => {
                                                            return (
                                                                <FormControlLabel
                                                                    key={`answer-label-${index}`}
                                                                    value={type.Answer}
                                                                    control={<Radio />}
                                                                    label={type.Answer}
                                                                />
                                                            );
                                                        })}
                                                    </RadioGroup>
                                                    {values.PreferredReligiousOrBeliefSystem === YES && (
                                                        <TextField
                                                            label="If yes, please share"
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            margin="normal"
                                                            name="PreferredReligiousOrBeliefSystemAnswer"
                                                            value={values.PreferredReligiousOrBeliefSystemAnswer || ''}
                                                            onChange={handleFieldChange}
                                                            InputProps={{
                                                                endAdornment:
                                                                    culturalConsiderationsInfo.IsAIPreferredReligiousOrBeliefSystemAnswer &&
                                                                    isAIIcon(culturalConsiderationsInfo.PreferredReligiousOrBeliefSystemAnswer),
                                                            }}
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                                    <Typography variant="h6" component="label">
                                                        Cultural Variables
                                                    </Typography>
                                                    <hr />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                                        {AssessmentQuestions.CulturalConsiderations_CulturalEthicConcerns}
                                                        {culturalConsiderationsInfo.IsAICulturalEthnicInfoConcerns && isAIIcon(culturalConsiderationsInfo.CulturalEthnicInfoConcerns)}
                                                    </FormLabel>
                                                </Grid>
                                                <Grid size={6}>
                                                    <TextField
                                                        label=""
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        multiline
                                                        rows={5}
                                                        placeholder="(experience of cultural bias as a stressor, immigration history/path, level of acculturation, time orientation, social orientation, verbal/non-verbal communication style, locus of control, ability, development, socioeconomic status, level of education, etc.)"
                                                        margin="normal"
                                                        name="CulturalEthnicInfoConcerns"
                                                        value={values.CulturalEthnicInfoConcerns || ''}
                                                        onChange={handleFieldChange}
                                                    />
                                                </Grid>
                                                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                                    <hr className="hr-lite" />
                                                </Grid>
                                                <Grid size={6}>
                                                    <Stack spacing={1} direction="column">
                                                        <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                                            {AssessmentQuestions.CulturalConsiderations_CulturalBarriers}
                                                            {culturalConsiderationsInfo.IsAICulturalBarriers && isAIIcon(culturalConsiderationsInfo.CulturalBarriers)}
                                                        </FormLabel>
                                                        <FormControlLabel
                                                            sx={{ marginLeft: 0 }}
                                                            control={
                                                                <Checkbox
                                                                    checked={values.NoBarriers}
                                                                    onChange={(e) => {
                                                                        setIsChanged(true);
                                                                        const { checked } = e.target;
                                                                        setFieldValue('NoBarriers', checked);
                                                                        setFieldValue('IsAICulturalBarriers', false);
                                                                        if (checked) {
                                                                            setFieldValue('CulturalBarriers', null);
                                                                        }
                                                                    }}
                                                                    size="small"
                                                                    inputProps={{ 'aria-label': 'controlled' }}
                                                                />
                                                            }
                                                            label="No barriers"
                                                        />
                                                    </Stack>
                                                </Grid>
                                                <Grid size={6}>
                                                    <Stack spacing={1}>
                                                        <TextField
                                                            placeholder="(language, transportation, financial, cultural, etc.)"
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            multiline
                                                            disabled={values.NoBarriers}
                                                            rows={4}
                                                            margin="normal"
                                                            name="CulturalBarriers.Name"
                                                            value={values.CulturalBarriers?.Name || ''}
                                                            onChange={handleFieldChange}
                                                        />
                                                    </Stack>
                                                </Grid>
                                            </Grid>

                                            <Grid justifyContent="flex-end" className="content-footer">
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    color="secondary"
                                                    sx={{ marginRight: 2 }}
                                                    disabled={isSaving}
                                                    onClick={() => dispatch(gotoPreviousMenu())}
                                                    startIcon={<ArrowBackIosIcon />}
                                                >
                                                    Previous
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    disabled={isSaving}
                                                    endIcon={isSaving ? <CircularProgress size={18} /> : <ArrowForwardIosIcon />}
                                                    onClick={handleSubmit}
                                                >
                                                    Next
                                                </Button>
                                            </Grid>
                                        </>
                                    )}

                                    {Boolean(culturalConsiderationsIsComplete(values) && culturalConsiderationsInfo.CreatedBy && !isChanged) ? (
                                        <AlertDialog
                                            open={showSaveAlert}
                                            title={`Cultural Considerations`}
                                            buttonsDirection="column-reverse"
                                            showBottomCenter={true}
                                            cancelText="Go Back"
                                            skipText={`Cultural Considerations Assessment Not Reviewed`}
                                            okText={`Agree With Cultural Considerations Assessment`}
                                            onConfirm={() => handleOnConfirmToNext(values, true)}
                                            onSkip={handleOnConfirmSkip}
                                            onClose={() => setShowSaveAlert(false)}
                                            isLoading={isSaving}
                                            message={`Last Cultural Considerations assessment completed on ${(() => {
                                                const d = toDate(values.UpdatedDate ?? values.CreatedDate);
                                                return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                            })()}.`}
                                        />
                                    ) : Boolean(culturalConsiderationsIsComplete(values)) ? (
                                        <AlertDialog
                                            open={showSaveAlert}
                                            title="Cultural Considerations"
                                            cancelText="Go Back"
                                            okText="Confirm"
                                            onConfirm={() => handleOnConfirmToNext(values, true)}
                                            onSkip={handleOnConfirmSkip}
                                            showBottomCenter={true}
                                            onClose={() => setShowSaveAlert(false)}
                                            isLoading={isSaving}
                                            message="I confirm and acknowledge that Cultural Considerations is completed."
                                        />
                                    ) : (
                                        <AlertDialog
                                            open={showSaveAlert}
                                            title="Cultural Considerations"
                                            showBottomCenter={true}
                                            cancelText="Go Back"
                                            skipText="Skip and continue"
                                            okText="Confirm"
                                            onConfirm={() => handleOnConfirmToNext(values, false)}
                                            onSkip={handleOnConfirmSkip}
                                            onClose={() => setShowSaveAlert(false)}
                                            isLoading={isSaving}
                                            renderBody={() =>
                                                BHDiagnosticConfirmationBody({
                                                    assessmentName: 'Cultural Considerations',
                                                    diagnosticType: engagementDraft.DiagnosticStatus?.DiagnosticType ?? DiagnosticType.None,
                                                })
                                            }
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    </Form>
                );
            }}
        </Formik>
    );
};
export default CulturalConsiderations;
